// Client-side helpers for Ory portal handoff deep-links. Backend at
// `GET /v1/auth/oidc/handoff-config` — 404 → hide all SSO deep-links.

import axios from 'axios';

export interface OidcHandoffConfig {
  handoff_endpoint: string;
  account_management_uri: string;
  handoff_actions_supported: string[];
  client_id: string;
}

/**
 * `apiBaseUrl` is the backend root WITHOUT `/v1`. Returns null on 404 or
 * transport errors — never throws.
 */
export async function fetchOidcHandoffConfig(
  apiBaseUrl: string
): Promise<OidcHandoffConfig | null> {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/v1/auth/oidc/handoff-config`;
  try {
    const { data } = await axios.get<OidcHandoffConfig>(url, {
      timeout: 5000,
      // Public endpoint, keep it cacheable.
      withCredentials: false,
    });
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    console.warn('[oidc] fetchOidcHandoffConfig failed:', err);
    return null;
  }
}

export interface BuildHandoffUrlParams {
  config: OidcHandoffConfig;
  action: string;
  /** Origin must match a registered redirect_uri / client_uri. */
  returnTo: string;
}

/**
 * Returns `null` when the action verb isn't supported — callers should fall
 * back to the account-management hub or hide the deep-link.
 */
export function buildIdpHandoffUrl({
  config,
  action,
  returnTo,
}: BuildHandoffUrlParams): string | null {
  if (!config.handoff_actions_supported.includes(action)) {
    return null;
  }
  const params = new URLSearchParams({
    referrer: config.client_id,
    referrer_uri: returnTo,
    action,
  });
  return `${config.handoff_endpoint}?${params.toString()}`;
}

export function buildAccountManagementUrl(config: OidcHandoffConfig): string {
  return config.account_management_uri;
}
